import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementBlock as _createElementBlock, withCtx as _withCtx, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  id: "show-roles",
  class: "d-flex flex-fill list-page"
}
const _hoisted_2 = ["id"]
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "d-flex flex-row justify-content-between" }
const _hoisted_6 = { class: "col" }
const _hoisted_7 = { class: "d-flex align-items-center" }
const _hoisted_8 = { class: "btn-label" }
const _hoisted_9 = { class: "btn-text" }
const _hoisted_10 = { class: "col d-flex flex-row align-items-center justify-content-center" }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = {
  key: 1,
  class: "d-flex flex-row align-items-center"
}
const _hoisted_13 = {
  key: 0,
  class: "loader-25 margin-left-3"
}
const _hoisted_14 = ["src"]
const _hoisted_15 = { class: "pagination-wrapper mt-1" }
const _hoisted_16 = { class: "loader-div-40" }
const _hoisted_17 = ["src"]

import {
  ElButton,
  ElCard,
  ElDropdown,
  ElDropdownItem,
  ElDropdownMenu,
  ElInput,
  ElDivider,
  ElTable,
  ElTableColumn,
} from "element-plus";
import { onMounted, ref } from "vue";
import { cloneDeep } from "lodash-es";
import { TYPE } from "vue-toastification";
import { router } from "@/core/router";
import { hasPermission } from "@/core/auth";
import {
  checkOrganizationRole,
  deleteRole,
  editRole,
  getRolesListing,
} from "@/role/services/role.service";
import { showAlert, showConfirm } from "@/core/popup";
import i18n from "@/core/i18n/i18n";
import { DEFAULT_LOGO, ASSETS_BASE_URL } from "@/core/logo";
import {
  getPreferenceValue,
  updateMultiplePreference,
  updateSinglePreference,
} from "@/core/preferences/preferences";
import OneTegPagination from "@/common/components/OneTegPagination.vue";
import { useDebouncedSearch } from "@/core/composables/useDebouncedSearch";
import { useLatestRequestGuard } from "@/core/composables/useLatestRequestGuard";


export default /*#__PURE__*/_defineComponent({
  __name: 'Roles',
  setup(__props) {

const { t } = i18n.global;
const rolesRequestGuard = useLatestRequestGuard();

const loadingRoles = ref(false);

const search = ref("");

const editMode = ref(false);

const roleList = ref([]);

const hoverId = ref("");
const animate = ref(false);

// "name" is a pre-existing (mismatched) preference default carried over from the
// generic Sort preference template used by other listing pages - Role has no "name"
// property (only "authority"), so any account whose stored preference still holds
// that stale value must not be allowed to use it here, even after the template fix.
const storedRolesSortField = getPreferenceValue("rolesSortField");
const sortField = ref(
  storedRolesSortField && storedRolesSortField !== "name"
    ? storedRolesSortField
    : "authority"
);

const sortAscending = ref(
  getPreferenceValue("rolesSortOrder")
    ? getPreferenceValue("rolesSortOrder")
    : "ascending"
);

const perPageRoles = ref(
  getPreferenceValue("pageSize") ? getPreferenceValue("pageSize") : 10
);

const currentPageRoles = ref(1);

const numberOfRoles = ref(0);

function sizeChanged(val) {
  perPageRoles.value = val;
  updateSinglePreference("pageSize", val);
  currentPageRoles.value = 1;
  getRoles();
}

function sortChanged(sort) {
  // Element Plus's 3-state column sort (ascending -> descending -> none) fires
  // sort-change with prop:null on the third click. Role has no "name" field, so
  // letting sortField go null would drop the query param and fall through to the
  // backend's shared default sortField ("name"), which errors for Role. Fall back
  // to the default sort instead of forwarding a null field.
  sortField.value = sort.prop || "authority";
  sortAscending.value = sort.prop ? sort.order : "ascending";
  updateMultiplePreference({
    rolesSortField: sortField.value,
    rolesSortOrder: sortAscending.value,
  });
  currentPageRoles.value = 1;
  getRoles();
}

const newObject = ref({
  id: undefined,
  authority: undefined,
  description: undefined,
});

function cellMouseEnter(row) {
  hoverId.value = row.id;
}

function cellMouseLeave() {
  hoverId.value = "";
}

const { onSearchInput } = useDebouncedSearch(() => {
  currentPageRoles.value = 1;
  getRoles();
});

async function getRoles() {
  const requestToken = rolesRequestGuard.start();
  loadingRoles.value = true;
  const params = {
    pageNumber: currentPageRoles.value - 1,
    pageSize: perPageRoles.value,
    sortField: sortField.value,
    ascending: sortAscending.value === "ascending",
    search: search.value,
  };
  try {
    const page = await getRolesListing(params);
    // OT-4928: discard a stale response superseded by a newer search/request.
    if (!rolesRequestGuard.isLatest(requestToken)) {
      return;
    }
    if (page) {
      roleList.value = page.data;
      numberOfRoles.value = page.totalElements;
    }
  } finally {
    if (rolesRequestGuard.isLatest(requestToken)) {
      loadingRoles.value = false;
    }
  }
}

function openEditMode(row) {
  animate.value = false;
  if (
    newObject.value.id !== row.id &&
    hasPermission("ROLES.LIST.INLINE.EDIT") &&
    row.customizable
  ) {
    newObject.value = cloneDeep(row);
    editMode.value = true;
  }
}

function closeEditMode() {
  editMode.value = false;
  newObject.value = {
    authority: undefined,
    id: undefined,
    description: undefined,
  };
}

function goToEditPage(row) {
  if (hasPermission("ROLES.LIST.EDIT") && newObject.value.id !== row.id) {
    router.push(`/roles/edit/${row.id}`);
  } else if (newObject.value.id === row.id) {
    animate.value = true;
    setTimeout(() => {
      animate.value = false;
    }, 2200);
  }
}

async function inLineEditRole(role) {
  let result = false;
  await editRole(role).then(async (response) => {
    if (response) {
      result = true;
    }
  });
  return result;
}

async function editSelectedRole() {
  if (
    editMode.value &&
    newObject.value.authority &&
    hasPermission("ROLES.LIST.INLINE.EDIT")
  ) {
    const result = await inLineEditRole(newObject.value);
    if (result) {
      editMode.value = false;
      newObject.value = {
        authority: undefined,
        id: undefined,
        description: undefined,
      };
      await getRoles();
      showAlert(TYPE.SUCCESS, t("Role.RoleSaved"));
    }
  } else if (hasPermission("ROLES.LIST.INLINE.EDIT")) {
    showAlert(TYPE.WARNING, t("Common.WarningMsg"));
  }
}

async function deleteSelectedRole(role) {
  if (hasPermission("ROLES.LIST.REMOVE")) {
    const count = await checkOrganizationRole(role.id);
    const message =
      count > 0
        ? t("Role.DeleteRoleWithUsers", { count, role: role.authority })
        : t("Role.DeleteRoleConfirmation", { role: role.authority });

    const isConfirmed = await showConfirm(
      "warning",
      t("Role.DeleteRole"),
      message
    );
    if (isConfirmed) {
      role.loading = true;
      const result = await deleteRole(role.id);
      role.loading = false;
      if (result) {
        await getRoles();
        showAlert(
          TYPE.SUCCESS,
          t("Role.RoleDeleted", { name: role.authority })
        );
      } else {
        showAlert(TYPE.ERROR, t("Role.DeleteFailed"));
      }
    }
  }
}

onMounted(() => {
  getRoles();
});

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(ElCard), { class: "card border-0 oneTeg-card oneTeg-card-body" }, {
      default: _withCtx(() => [
        _createVNode(_unref(ElTable), {
          class: "oneTeg-table",
          data: roleList.value,
          "empty-text": loadingRoles.value ? ' ' : 'No Data',
          "default-sort": { prop: sortField.value, order: sortAscending.value },
          onRowClick: goToEditPage,
          onCellMouseEnter: cellMouseEnter,
          onCellMouseLeave: cellMouseLeave,
          onSortChange: sortChanged,
          "row-class-name": 
          _unref(hasPermission)('ROLES.LIST.EDIT')
            ? 'oneTeg-table-row-permission'
            : 'oneTeg-table-row-no-permission'
        ,
          height: "97%"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(ElTableColumn), {
              label: _unref(t)('Common.Name'),
              prop: "authority",
              sortable: "custom",
              width: "auto"
            }, {
              default: _withCtx((scope) => [
                (editMode.value && scope.row.id === newObject.value.id)
                  ? (_openBlock(), _createBlock(_unref(ElInput), {
                      key: 0,
                      placeholder: "t('Common.Name')",
                      id: 'name-input-' + scope.$index,
                      onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
                      modelValue: newObject.value.authority,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newObject.value.authority) = $event))
                    }, null, 8, ["id", "modelValue"]))
                  : (_openBlock(), _createElementBlock("div", {
                      key: 1,
                      id: 'name-' + scope.$index
                    }, [
                      _createElementVNode("strong", null, _toDisplayString(scope.row.authority), 1),
                      (
                  _unref(hasPermission)('ROLES.LIST.INLINE.EDIT') &&
                  scope.row.id === hoverId.value
                )
                        ? (_openBlock(), _createElementBlock("span", {
                            key: 0,
                            class: "pencil-span",
                            onClick: _withModifiers(($event: any) => (openEditMode(scope.row)), ["stop"])
                          }, [
                            _createVNode(_component_font_awesome_icon, { icon: "pencil-alt" })
                          ], 8, _hoisted_3))
                        : _createCommentVNode("", true)
                    ], 8, _hoisted_2))
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_unref(ElTableColumn), {
              label: _unref(t)('Common.Description'),
              prop: "description",
              width: "auto"
            }, {
              default: _withCtx((scope) => [
                (editMode.value && scope.row.id === newObject.value.id)
                  ? (_openBlock(), _createBlock(_unref(ElInput), {
                      key: 0,
                      placeholder: _unref(t)('Common.Description'),
                      onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"])),
                      modelValue: newObject.value.description,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newObject.value.description) = $event))
                    }, null, 8, ["placeholder", "modelValue"]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _createElementVNode("strong", null, _toDisplayString(scope.row.description), 1)
                    ]))
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_unref(ElTableColumn), {
              prop: "actions",
              width: "300px"
            }, {
              header: _withCtx(() => [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_unref(ElInput), {
                    modelValue: search.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((search).value = $event)),
                    size: "mini",
                    id: "search-input",
                    class: "oneTeg-search-input max-w-170",
                    onInput: _unref(onSearchInput),
                    placeholder: _unref(t)('Common.TypeToSearch')
                  }, null, 8, ["modelValue", "onInput", "placeholder"]),
                  _createVNode(_unref(ElDivider), {
                    direction: "vertical",
                    class: "v-divider-stretch",
                    "aria-hidden": "true"
                  }),
                  _createElementVNode("div", _hoisted_6, [
                    _createVNode(_unref(ElButton), {
                      id: "create-button",
                      class: "no-border btn-oneTeg2-table-create btn-labeled",
                      size: "mini",
                      type: "primary",
                      onClick: _cache[5] || (_cache[5] = _withModifiers(($event: any) => (_unref(router).push('/roles/create')), ["stop"]))
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("span", _hoisted_7, [
                          _createElementVNode("span", _hoisted_8, [
                            _createVNode(_component_font_awesome_icon, { icon: "plus" })
                          ]),
                          _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(t)("Role.CreateRole")), 1)
                        ])
                      ]),
                      _: 1
                    })
                  ])
                ])
              ]),
              default: _withCtx((scope) => [
                _createElementVNode("div", _hoisted_10, [
                  (
                  editMode.value &&
                  scope.row.id === newObject.value.id &&
                  _unref(hasPermission)('ROLES.LIST.INLINE.EDIT')
                )
                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                        _createElementVNode("div", {
                          class: _normalizeClass({ animateBtn: animate.value })
                        }, [
                          _createVNode(_unref(ElButton), {
                            class: "btn-oneTeg max-w-42-5",
                            id: 'inline-edit-save-' + scope.$index,
                            size: "mini",
                            type: "primary",
                            onClick: _withModifiers(editSelectedRole, ["stop"])
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_font_awesome_icon, { icon: "check" })
                            ]),
                            _: 2
                          }, 1032, ["id"]),
                          _createVNode(_unref(ElButton), {
                            class: "btn-oneTeg margin-left-3",
                            "native-type": "submit",
                            id: 'inline-edit-back-' + scope.$index,
                            size: "mini",
                            type: "primary",
                            onClick: _withModifiers(closeEditMode, ["stop"])
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_font_awesome_icon, { icon: "times" })
                            ]),
                            _: 2
                          }, 1032, ["id"])
                        ], 2)
                      ]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_12, [
                        _createVNode(_unref(ElDropdown), null, {
                          dropdown: _withCtx(() => [
                            _createVNode(_unref(ElDropdownMenu), null, {
                              default: _withCtx(() => [
                                (_unref(hasPermission)('ROLES.LIST.EDIT'))
                                  ? (_openBlock(), _createBlock(_unref(ElDropdownItem), {
                                      key: 0,
                                      id: 'edit-button-' + scope.$index,
                                      onClick: _withModifiers(($event: any) => (_unref(router).push('/roles/edit/' + scope.row.id)), ["stop"])
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_unref(t)("Common.Edit")), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["id", "onClick"]))
                                  : _createCommentVNode("", true),
                                (_unref(hasPermission)('ROLES.LIST.REMOVE'))
                                  ? (_openBlock(), _createBlock(_unref(ElDropdownItem), {
                                      key: 1,
                                      id: 'remove-button-' + scope.$index,
                                      onClick: _withModifiers(($event: any) => (deleteSelectedRole(scope.row)), ["stop"])
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_unref(t)("Common.Delete")), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["id", "onClick"]))
                                  : _createCommentVNode("", true)
                              ]),
                              _: 2
                            }, 1024)
                          ]),
                          default: _withCtx(() => [
                            _createVNode(_unref(ElButton), {
                              class: "no-border btn-oneTeg show-on-row-hover",
                              size: "mini",
                              onClick: _cache[6] || (_cache[6] = _withModifiers(() => {}, ["stop"])),
                              id: 'cog-icon-' + scope.$index,
                              type: "primary"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_font_awesome_icon, { icon: "cog" })
                              ]),
                              _: 2
                            }, 1032, ["id"])
                          ]),
                          _: 2
                        }, 1024),
                        (scope.row.loading)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                              _createElementVNode("img", {
                                src: `${_unref(ASSETS_BASE_URL)}/${_unref(DEFAULT_LOGO)}.png`,
                                alt: "Loading...",
                                class: "loader-logo-25"
                              }, null, 8, _hoisted_14)
                            ]))
                          : _createCommentVNode("", true)
                      ]))
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["data", "empty-text", "default-sort", "row-class-name"]),
        _createElementVNode("div", _hoisted_15, [
          _createVNode(OneTegPagination, {
            "page-sizes": [10, 25, 50, 100],
            "page-size": perPageRoles.value,
            "onUpdate:pageSize": _cache[7] || (_cache[7] = ($event: any) => ((perPageRoles).value = $event)),
            total: numberOfRoles.value,
            "current-page": currentPageRoles.value,
            "onUpdate:currentPage": _cache[8] || (_cache[8] = ($event: any) => ((currentPageRoles).value = $event)),
            background: true,
            small: true,
            onSizeChange: sizeChanged,
            onCurrentChange: getRoles,
            layout: 'total, sizes, prev, pager, next'
          }, null, 8, ["page-size", "total", "current-page"])
        ])
      ]),
      _: 1
    }),
    _withDirectives(_createElementVNode("div", _hoisted_16, [
      _createElementVNode("img", {
        src: `${_unref(ASSETS_BASE_URL)}/${_unref(DEFAULT_LOGO)}.png`,
        alt: "Loading...",
        class: "loader-logo-40"
      }, null, 8, _hoisted_17)
    ], 512), [
      [_vShow, loadingRoles.value]
    ])
  ]))
}
}

})